import { useState } from "react";
import { Plus, Trash2, Loader2, RefreshCw, CheckCircle2, AlertCircle, Wallet } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { API, getAuthHeaders, formatDate, formatMontant } from "./helpers";
import { useConfigList, listToOptions } from "../../../hooks/useConfigList";
import { toast } from "sonner";

// Modes de règlement AMO = liste config visite_mode_paiement (le règlement AMO EST
// le paiement de la visite — décision JJ). Repli = les 3 valeurs canoniques.
const MODE_FALLBACK = [
  { value: "cheque_visite", label: "Chèque à la visite" },
  { value: "virement_apres_visite", label: "Virement après la visite" },
  { value: "paye_par_installateur", label: "Payé par installateur" },
];

function StatutBadge({ statut }) {
  const map = {
    paye:     { label: "Payé",       cls: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-[var(--oh-success-border)]" },
    partiel:  { label: "Partiel",    cls: "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]" },
    non_paye: { label: "Non payé",   cls: "bg-zinc-500/20 text-[var(--oh-text-secondary)] border-[var(--oh-border)]" },
  };
  const s = map[statut] || { label: statut || "—", cls: "bg-zinc-500/20 text-[var(--oh-text-secondary)]" };
  return <Badge className={`${s.cls} border text-xs`} data-testid="paiements-statut">{s.label}</Badge>;
}

// Sets de statuts Pennylane reconnus côté front (cohérent avec
// backend compute_statut_paiement).
//
// Refonte P0 (2026-06-07) : le prédicat booléen canonique "facture payée" vit
// dans helpers.js:isFacturePayee (miroir backend facture_payee). On NE l'injecte
// PAS ici à dessein : deriveStatut est un calcul À 3 ÉTATS (paye/partiel/non) à
// sémantique d'override Pennylane (set PENNY_PAID plus large + réconciliation
// montant), volontairement conservé intact — l'y substituer changerait des
// résultats (cf. garde-fou anti-régression du LOT P0).
const PENNY_PAID = new Set(["paid", "paye", "payée", "facture_payee", "totalement_paye"]);
const PENNY_PARTIAL = new Set([
  "partially_paid", "partial", "partiel", "partiellement_paye",
  "partiellement_payee", "partiellement_payée",
]);

function deriveStatut({ paiements, montantAmo, statutPaiement, statutFacturation, pennylaneStatus }) {
  // Pennylane gagne en override (cohérent backend) — accepte plusieurs
  // sources : statut_facturation (cache), pennylane_invoice.payment_status.
  const pl = String(statutFacturation || pennylaneStatus || "").trim().toLowerCase();
  const total = (paiements || []).reduce((s, p) => s + (parseFloat(p.montant) || 0), 0);
  const ttc = parseFloat(montantAmo) || 0;

  if (PENNY_PAID.has(pl)) return "paye";
  if (PENNY_PARTIAL.has(pl)) {
    if (ttc > 0 && total >= ttc - 0.01) return "paye";
    return "partiel";
  }
  // Calcul local depuis paiements[]
  if (total <= 0) return statutPaiement || "non_paye";
  if (ttc <= 0) return "partiel";
  if (total >= ttc - 0.01) return "paye";
  return "partiel";
}

export function PaiementsMultiLignes({ dossierId, tenantId, paiements: initialPaiements, montantAmo, statutPaiement, statutFacturation, pennylaneStatus, onChange }) {
  const [paiements, setPaiements] = useState(initialPaiements || []);
  const [statut, setStatut] = useState(() => deriveStatut({
    paiements: initialPaiements || [],
    montantAmo, statutPaiement, statutFacturation, pennylaneStatus,
  }));
  const [adding, setAdding] = useState(false);
  const [actionIdx, setActionIdx] = useState(null);
  const [form, setForm] = useState({ date: "", montant: "", mode: "cheque_visite", reference: "" });

  // Modes depuis la config tenant (visite_mode_paiement), repli sur les 3 canoniques.
  const { items: modeItems } = useConfigList("visite_mode_paiement");
  const MODES = modeItems.length ? listToOptions(modeItems) : MODE_FALLBACK;

  const totalPaye = paiements.reduce((s, p) => s + (parseFloat(p.montant) || 0), 0);
  const ttc = parseFloat(montantAmo) || 0;

  const refreshFromResponse = (data) => {
    if (Array.isArray(data?.paiements)) setPaiements(data.paiements);
    if (typeof data?.statut_paiement === "string") setStatut(data.statut_paiement);
    if (typeof onChange === "function") onChange(data);
  };

  // Pour les routes qui ne renvoient que metadata, on re-fetch les paiements depuis le dossier
  const reload = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/${dossierId}`, { headers, credentials: "include" });
      // fallback : route dossier générique pas dispo, on garde l'état local
    } catch {}
  };

  const handleAdd = async () => {
    const montant = parseFloat(String(form.montant).replace(",", ".")) || 0;
    if (!form.date) { toast.error("Date requise"); return; }
    if (montant <= 0) { toast.error("Montant > 0 requis"); return; }
    setAdding(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/paiements`, {
        method: "POST", headers, credentials: "include",
        body: JSON.stringify({
          date: form.date,
          montant,
          mode: form.mode,
          reference: form.reference || null,
          source: "manual",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const newPaiements = [...paiements, data.paiement];
        setPaiements(newPaiements);
        // Statut dérivé : Pennylane override (statut_facturation) gagne sur
        // le statut_paiement renvoyé par l'API si Pennylane indique paid.
        setStatut(deriveStatut({
          paiements: newPaiements, montantAmo,
          statutPaiement: data.statut_paiement,
          statutFacturation, pennylaneStatus,
        }));
        if (typeof onChange === "function") onChange(data);
        toast.success("Règlement ajouté");
        setForm({ date: "", montant: "", mode: "cheque_visite", reference: "" });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur ajout règlement");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (idx) => {
    setActionIdx(idx);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/paiements/${idx}`, {
        method: "DELETE", headers, credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        const newPaiements = paiements.filter((_, i) => i !== idx);
        setPaiements(newPaiements);
        setStatut(deriveStatut({
          paiements: newPaiements, montantAmo,
          statutPaiement: data.statut_paiement,
          statutFacturation, pennylaneStatus,
        }));
        if (typeof onChange === "function") onChange(data);
        toast.success("Règlement supprimé");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur suppression");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setActionIdx(null);
    }
  };

  return (
    <div className="rounded-lg p-4 space-y-3" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }} data-testid="paiements-multilignes">
      <div className="flex items-center justify-between">
        <h5 className="font-medium text-sm flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <Wallet className="w-4 h-4 text-cyan-400" />
          Règlements AMO
          <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>
            ({paiements.length} ligne{paiements.length > 1 ? "s" : ""})
          </span>
        </h5>
        <StatutBadge statut={statut} />
      </div>

      {/* Liste des paiements */}
      {paiements.length === 0 ? (
        <p className="text-sm text-center py-3" style={{ color: "var(--oh-text-muted)" }}>
          Aucun règlement enregistré.
        </p>
      ) : (
        <div className="space-y-1.5">
          {paiements.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded" style={{ background: "var(--oh-bg)", border: "1px solid var(--oh-border)" }} data-testid={`paiement-row-${idx}`}>
              <span className="text-sm w-24" style={{ color: "var(--oh-text)" }}>{formatDate(p.date)}</span>
              <span className="text-sm font-medium w-28 text-right" style={{ color: "var(--oh-text)" }}>{formatMontant(p.montant)}</span>
              <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>
                {MODES.find(m => m.value === p.mode)?.label || p.mode || "—"}
              </span>
              {p.reference && (
                <span className="text-xs italic flex-1" style={{ color: "var(--oh-text-muted)" }}>
                  réf. {p.reference}
                </span>
              )}
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)] h-7 px-2"
                disabled={actionIdx === idx}
                onClick={() => handleDelete(idx)}
                data-testid={`paiement-delete-${idx}`}
              >
                {actionIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire d'ajout inline */}
      <div className="flex flex-wrap items-center gap-2 pt-2" style={{ borderTop: "1px solid var(--oh-border)" }}>
        <Input
          type="date"
          value={form.date}
          onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
          className="w-36"
          style={{ background: "var(--oh-bg)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid="paiement-form-date"
          aria-label="Date du règlement"
        />
        <Input
          type="text"
          inputMode="decimal"
          placeholder="Montant"
          value={form.montant}
          onChange={(e) => setForm(prev => ({ ...prev, montant: e.target.value.replace(/[^0-9.,]/g, "") }))}
          className="w-28 text-right"
          style={{ background: "var(--oh-bg)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid="paiement-form-montant"
          aria-label="Montant du règlement"
        />
        <select
          value={form.mode}
          onChange={(e) => setForm(prev => ({ ...prev, mode: e.target.value }))}
          className="text-sm px-2 py-2 rounded border h-9"
          style={{ background: "var(--oh-bg)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid="paiement-form-mode"
          aria-label="Mode de paiement"
        >
          {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <Input
          type="text"
          placeholder="Réf. (n° chèque…)"
          value={form.reference}
          onChange={(e) => setForm(prev => ({ ...prev, reference: e.target.value }))}
          className="flex-1 min-w-[120px]"
          style={{ background: "var(--oh-bg)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}
          data-testid="paiement-form-reference"
          aria-label="Référence du règlement"
        />
        <Button
          size="sm"
          className="bg-cyan-600 hover:bg-cyan-700 text-[var(--oh-accent-contrast)]"
          onClick={handleAdd}
          disabled={adding}
          data-testid="paiement-form-add"
        >
          {adding ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
          Ajouter
        </Button>
      </div>

      {/* Footer total */}
      <div className="flex items-center justify-between pt-2 px-1" style={{ borderTop: "1px solid var(--oh-border)" }}>
        <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
          Montant TTC AMO :&nbsp;
          <span className="font-medium" style={{ color: "var(--oh-text)" }}>{ttc > 0 ? formatMontant(ttc) : "—"}</span>
        </div>
        <div className="text-sm">
          <span style={{ color: "var(--oh-text-muted)" }}>Total payé : </span>
          <span className="font-medium" style={{ color: "var(--oh-text)" }} data-testid="paiements-total">
            {formatMontant(totalPaye)}
          </span>
          {ttc > 0 && (
            <span className="text-xs ml-2" style={{ color: "var(--oh-text-muted)" }}>
              ({Math.round((totalPaye / ttc) * 100)}%)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
